/**
 * PhaseTimeline Component
 *
 * A reusable component for visualizing movement phases as a horizontal timeline.
 * Supports both read-only viewing and interactive editing of phase boundaries.
 *
 * Features:
 * - Color-coded phases by importance level
 * - Interactive hover tooltips with phase details
 * - Click to select and highlight phases
 * - Optional drag handles for editing phase boundaries
 * - Responsive design that adapts to container width
 * - Frame number labels and indicators
 * - Current playback position marker
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { getImportanceColor, IMPORTANCE_COLORS } from '@/utils/colors';

// Types
export interface Phase {
  phase_id: string;
  phase_type: 'preparation' | 'execution' | 'follow_through' | 'recovery' | 'transition' | 'custom';
  label: string;
  start_frame: number;
  end_frame: number;
  importance: 'critical' | 'high' | 'medium' | 'low';
  description?: string;
}

export interface PhaseTimelineProps {
  phases: Phase[];
  totalFrames: number;
  fps: number;
  currentFrame?: number;
  editable?: boolean;
  onPhaseClick?: (phase: Phase) => void;
  onBoundaryChange?: (phaseId: string, newStart: number, newEnd: number) => void;
  className?: string;
  height?: number;
  showFrameNumbers?: boolean;
  showTimeLabels?: boolean;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  phase: Phase | null;
}

interface DragState {
  active: boolean;
  phaseId: string | null;
  boundary: 'start' | 'end' | null;
  startX: number;
}

export const PhaseTimeline: React.FC<PhaseTimelineProps> = ({
  phases,
  totalFrames,
  fps,
  currentFrame = 0,
  editable = false,
  onPhaseClick,
  onBoundaryChange,
  className = '',
  height = 80,
  showFrameNumbers = true,
  showTimeLabels = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    phase: null,
  });
  const [dragState, setDragState] = useState<DragState>({
    active: false,
    phaseId: null,
    boundary: null,
    startX: 0,
  });

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Convert frame to pixel position
  const frameToX = useCallback(
    (frame: number): number => {
      return (frame / totalFrames) * containerWidth;
    },
    [totalFrames, containerWidth]
  );

  // Convert pixel position to frame
  const xToFrame = useCallback(
    (x: number): number => {
      return Math.round((x / containerWidth) * totalFrames);
    },
    [totalFrames, containerWidth]
  );

  // Convert frame to time string
  const frameToTime = useCallback(
    (frame: number): string => {
      const seconds = frame / fps;
      const minutes = Math.floor(seconds / 60);
      const secs = (seconds % 60).toFixed(1);
      return minutes > 0 ? `${minutes}:${secs.padStart(4, '0')}` : `${secs}s`;
    },
    [fps]
  );

  // Handle phase click
  const handlePhaseClick = (phase: Phase, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedPhaseId(phase.phase_id);
    if (onPhaseClick) {
      onPhaseClick(phase);
    }
  };

  // Handle mouse enter for tooltip
  const handlePhaseMouseEnter = (phase: Phase, event: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setTooltip({
      visible: true,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      phase,
    });
  };

  // Handle mouse move for tooltip
  const handlePhaseMouseMove = (event: React.MouseEvent) => {
    if (!tooltip.visible) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setTooltip((prev) => ({
      ...prev,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }));
  };

  // Handle mouse leave for tooltip
  const handlePhaseMouseLeave = () => {
    setTooltip({
      visible: false,
      x: 0,
      y: 0,
      phase: null,
    });
  };

  // Handle boundary drag start
  const handleBoundaryMouseDown = (
    phaseId: string,
    boundary: 'start' | 'end',
    event: React.MouseEvent
  ) => {
    if (!editable) return;

    event.stopPropagation();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setDragState({
      active: true,
      phaseId,
      boundary,
      startX: event.clientX - rect.left,
    });
  };

  // Handle boundary drag move
  useEffect(() => {
    if (!dragState.active) return;

    const handleMouseMove = (event: MouseEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = event.clientX - rect.left;
      const frame = xToFrame(x);

      const phase = phases.find((p) => p.phase_id === dragState.phaseId);
      if (!phase || !onBoundaryChange) return;

      // Calculate new boundaries with constraints
      let newStart = phase.start_frame;
      let newEnd = phase.end_frame;

      if (dragState.boundary === 'start') {
        newStart = Math.max(0, Math.min(frame, phase.end_frame - 1));
      } else if (dragState.boundary === 'end') {
        newEnd = Math.max(phase.start_frame + 1, Math.min(frame, totalFrames));
      }

      // Check for overlaps with adjacent phases
      const sortedPhases = [...phases].sort((a, b) => a.start_frame - b.start_frame);
      const phaseIndex = sortedPhases.findIndex((p) => p.phase_id === phase.phase_id);

      if (dragState.boundary === 'start' && phaseIndex > 0) {
        const prevPhase = sortedPhases[phaseIndex - 1];
        newStart = Math.max(newStart, prevPhase.end_frame);
      }

      if (dragState.boundary === 'end' && phaseIndex < sortedPhases.length - 1) {
        const nextPhase = sortedPhases[phaseIndex + 1];
        newEnd = Math.min(newEnd, nextPhase.start_frame);
      }

      onBoundaryChange(phase.phase_id, newStart, newEnd);
    };

    const handleMouseUp = () => {
      setDragState({
        active: false,
        phaseId: null,
        boundary: null,
        startX: 0,
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, phases, totalFrames, xToFrame, onBoundaryChange]);

  // Get color for phase (prioritize importance over type)
  const getPhaseColor = (phase: Phase): string => {
    return getImportanceColor(phase.importance);
  };

  // Render phase bars
  const renderPhaseBars = () => {
    return phases.map((phase) => {
      const startX = frameToX(phase.start_frame);
      const endX = frameToX(phase.end_frame);
      const width = endX - startX;
      const isSelected = phase.phase_id === selectedPhaseId;

      return (
        <div
          key={phase.phase_id}
          className={`absolute cursor-pointer transition-all duration-200 ${
            isSelected ? 'ring-2 ring-white ring-offset-2 ring-offset-dark-base z-10' : ''
          }`}
          style={{
            left: `${startX}px`,
            width: `${width}px`,
            height: `${height}px`,
            backgroundColor: getPhaseColor(phase),
            opacity: isSelected ? 1 : 0.9,
          }}
          onClick={(e) => handlePhaseClick(phase, e)}
          onMouseEnter={(e) => handlePhaseMouseEnter(phase, e)}
          onMouseMove={handlePhaseMouseMove}
          onMouseLeave={handlePhaseMouseLeave}
        >
          {/* Phase label */}
          {width > 60 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-xs font-medium px-2 pointer-events-none">
              <div className="truncate max-w-full">{phase.label}</div>
              {showFrameNumbers && width > 100 && (
                <div className="text-[10px] opacity-80 mt-0.5">
                  {phase.start_frame}-{phase.end_frame}
                </div>
              )}
            </div>
          )}

          {/* Drag handles for editable mode */}
          {editable && (
            <>
              <div
                className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize bg-white bg-opacity-0 hover:bg-opacity-30 transition-colors"
                onMouseDown={(e) => handleBoundaryMouseDown(phase.phase_id, 'start', e)}
              />
              <div
                className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize bg-white bg-opacity-0 hover:bg-opacity-30 transition-colors"
                onMouseDown={(e) => handleBoundaryMouseDown(phase.phase_id, 'end', e)}
              />
            </>
          )}
        </div>
      );
    });
  };

  // Render current position indicator
  const renderCurrentPosition = () => {
    if (currentFrame < 0 || currentFrame > totalFrames) return null;

    const x = frameToX(currentFrame);

    return (
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-red-600 z-20 pointer-events-none"
        style={{ left: `${x}px` }}
      >
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-600 rounded-full" />
      </div>
    );
  };

  // Render tooltip
  const renderTooltip = () => {
    if (!tooltip.visible || !tooltip.phase) return null;

    const phase = tooltip.phase;
    const duration = phase.end_frame - phase.start_frame;

    return (
      <div
        className="absolute z-30 bg-dark-base border border-border-strong text-text-primary text-xs rounded-lg shadow-xl p-3 pointer-events-none min-w-[200px]"
        style={{
          left: `${Math.min(tooltip.x, containerWidth - 220)}px`,
          top: `${tooltip.y - 120}px`,
        }}
      >
        <div className="font-semibold mb-1">{phase.label}</div>
        <div className="space-y-1 text-text-secondary">
          <div>
            <span className="text-text-muted">Type:</span>{' '}
            {phase.phase_type.replace('_', ' ')}
          </div>
          <div>
            <span className="text-text-muted">Frames:</span> {phase.start_frame} - {phase.end_frame}{' '}
            ({duration} frames)
          </div>
          {showTimeLabels && (
            <div>
              <span className="text-text-muted">Duration:</span> {frameToTime(duration)}
            </div>
          )}
          <div>
            <span className="text-text-muted">Importance:</span>{' '}
            <span
              className="inline-block px-2 py-0.5 rounded text-[10px] font-medium"
              style={{
                backgroundColor: getPhaseColor(phase),
                color: 'white',
              }}
            >
              {phase.importance}
            </span>
          </div>
          {phase.description && (
            <div className="mt-2 pt-2 border-t border-border-subtle">
              <div className="text-text-muted text-[10px] mb-1">Description:</div>
              <div className="text-text-primary">{phase.description}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render frame markers
  const renderFrameMarkers = () => {
    if (!showFrameNumbers) return null;

    const markerInterval = Math.ceil(totalFrames / 10);
    const markers = [];

    for (let i = 0; i <= totalFrames; i += markerInterval) {
      const x = frameToX(i);
      markers.push(
        <div
          key={i}
          className="absolute -bottom-5 text-[10px] text-text-muted transform -translate-x-1/2"
          style={{ left: `${x}px` }}
        >
          {i}
        </div>
      );
    }

    return markers;
  };

  if (containerWidth === 0) {
    return (
      <div ref={containerRef} className={`relative w-full ${className}`}>
        <div className="bg-dark-elevated-2 rounded animate-pulse" style={{ height: `${height}px` }} />
      </div>
    );
  }

  return (
    <div className={`relative w-full ${className}`}>
      {/* Main timeline container */}
      <div
        ref={containerRef}
        className="relative bg-dark-elevated-2 rounded overflow-visible"
        style={{ height: `${height}px` }}
      >
        {/* Background grid */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute top-0 bottom-0 w-px bg-text-primary"
              style={{ left: `${(i * 100) / 10}%` }}
            />
          ))}
        </div>

        {/* Phase bars */}
        {renderPhaseBars()}

        {/* Current position indicator */}
        {renderCurrentPosition()}

        {/* Tooltip */}
        {renderTooltip()}
      </div>

      {/* Frame markers */}
      {showFrameNumbers && <div className="relative mt-2 h-5">{renderFrameMarkers()}</div>}

      {/* Legend */}
      {phases.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <div className="font-medium text-text-secondary">Importance:</div>
          {Object.entries(IMPORTANCE_COLORS).map(([importance, color]) => (
            <div key={importance} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: color }}
              />
              <span className="text-text-muted capitalize">{importance}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhaseTimeline;
